import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Modal, CompanyInfoFields, EMPTY_COMPANY_INFO_FORM, validateCompanyInfo } from '../components';
import api from '../services/api';
import '../styles/CompanyInformation.scss';
import { useRole } from '../utils/RoleContext';
import { useEntityConfig } from '../utils/EntityConfigContext';

const CompanyInformation = () => {
  const navigate = useNavigate();
  const { role } = useRole();
  const canEdit = role === 'Consultant';
  const [form, setForm] = useState(EMPTY_COMPANY_INFO_FORM);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved'
  const [errorModal, setErrorModal] = useState(null);
  const { refreshConfig } = useEntityConfig();

  const companyId = localStorage.getItem('company_id');
  const [archived, setArchived] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [archiveSaving, setArchiveSaving] = useState(false);
  const [archiveNotice, setArchiveNotice] = useState('');

  useEffect(() => {
    api.get('/core/company-info/')
    .then(res => {
      if (res.data && Object.keys(res.data).length > 0){
        setForm(prev => ({ ...prev, ...res.data}));
      }
    })
    .catch(err => console.error('Failed to load company info:', err))
    .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    if (!canEdit || !companyId) return;
    api.get(`/core/companies/${companyId}/`)
      .then(res => setArchived(!!res.data.archived))
      .catch(err => console.error('Failed to load archive status:', err));
  }, [canEdit, companyId]);

  const handleToggleArchive = async () => {
    setArchiveSaving(true);
    try {
      const res = await api.post(`/core/companies/${companyId}/archive/`, { archived: !archived });
      setArchived(res.data.archived);
      setArchiveNotice(res.data.sharepoint_warning || '');
    } catch (err) {
      setArchiveNotice(err.response?.data?.error || 'Failed to update archive status. Please try again.');
    } finally {
      setArchiveSaving(false);
      setShowArchiveConfirm(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validateCompanyInfo(form);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSaveStatus('saving');
    try {
      await api.post('/core/company-info/save/', form);
      refreshConfig();
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (err) {
      console.log(err);
      const msg = err.response?.data?.error || 'Failed to save. Please try again.';
      setErrorModal(msg);
      setSaveStatus('idle');
    }
  };

  if (isLoading) {
    return (
      <div className="ci-container">
        <div className="ci-loading">Loading company information...</div>
      </div>
    );
  }

  return (
    <div className="ci-container">
      <div className="ci-header">
        <button className="ci-back" onClick={() => navigate('/finance-statutory')}>← Back</button>
        <div className="ci-header-text">
          <h1>Company Information</h1>
          <p></p>
        </div>
      </div>

      <div className="ci-body">
        <form onSubmit={handleSubmit}>
          <CompanyInfoFields form={form} errors={errors} onChange={handleChange} canEdit={canEdit} />

          <div className="ci-actions">
            <Button type="submit" disabled={saveStatus === 'saving' || !canEdit}>
              {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? 'Saved' : 'Save Company Information'}
            </Button>
            <Button type="button" variant="secondary" onClick={() => navigate('/finance-statutory')} disabled={saveStatus === 'saving'}>
              Cancel
            </Button>
            {canEdit && companyId && (
              <Button type="button" variant="secondary" onClick={() => setShowArchiveConfirm(true)}>
                {archived ? 'Unarchive client' : 'Archive client'}
              </Button>
            )}
          </div>
        </form>
      </div>

      {errorModal && (
        <div className="ci-modal-overlay">
          <div className="ci-modal">
            <h3 className="ci-modal-title">⚠ Save Failed</h3>
            <p className="ci-modal-body">{errorModal}</p>
            <div className="ci-modal-actions">
              <button className="ci-modal-btn ci-modal-btn--primary" onClick={() => setErrorModal(null)}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      <Modal
        isOpen={showArchiveConfirm}
        onClose={() => setShowArchiveConfirm(false)}
        title={archived ? 'Unarchive client?' : 'Archive client?'}
        footer={
          <>
            <Button type="button" variant="secondary" onClick={() => setShowArchiveConfirm(false)} disabled={archiveSaving}>
              Cancel
            </Button>
            <Button type="button" onClick={handleToggleArchive} disabled={archiveSaving}>
              {archiveSaving ? 'Saving...' : archived ? 'Unarchive' : 'Archive'}
            </Button>
          </>
        }
      >
        <p>
          {archived
            ? 'This client will move back to your main company list, its client users will regain access, and its SharePoint folder will move out of Archived.'
            : 'This client will move into the Archived folder. Consultants can still open and edit it, but its client users (BEE Manager, HR, Logistics, Finance) will lose access. Its SharePoint folder will move to Archived.'}
        </p>
      </Modal>

      <Modal isOpen={!!archiveNotice} onClose={() => setArchiveNotice('')} title="⚠ Note">
        <p>{archiveNotice}</p>
      </Modal>
    </div>
  );
};

export default CompanyInformation;
